
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function BookCallPage() {
  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script when component unmounts
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-[#1B365D] to-[#2BB6A3]">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Schedule Your Free Strategy Call
              </h1>
              <p className="text-xl md:text-2xl mb-8 opacity-90">
                Book a 30-minute call with James Hansen to discuss your revenue growth challenges and opportunities
              </p>
              <div className="grid md:grid-cols-3 gap-6 mt-12">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <i className="ri-time-line text-3xl text-[#D4A017] mb-4"></i>
                  <h3 className="font-semibold mb-2">30 Minutes</h3>
                  <p className="text-sm opacity-80">Focused strategy session</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <i className="ri-video-chat-line text-3xl text-[#D4A017] mb-4"></i>
                  <h3 className="font-semibold mb-2">Video Call</h3>
                  <p className="text-sm opacity-80">Zoom meeting included</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <i className="ri-gift-line text-3xl text-[#D4A017] mb-4"></i>
                  <h3 className="font-semibold mb-2">Completely Free</h3>
                  <p className="text-sm opacity-80">No strings attached</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Calendly Widget Section */}
        <section className="py-16 bg-[#F6F7F9]">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="p-8 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-[#1B365D] mb-4">
                    Choose Your Preferred Time
                  </h2>
                  <p className="text-[#2A2D34]">
                    Select a time that works best for you. You'll receive a confirmation email with the meeting details.
                  </p>
                </div>
                
                <div className="p-8">
                  <div 
                    className="calendly-inline-widget" 
                    data-url="https://calendly.com/jameslawrencehansen/30min"
                    style={{ minWidth: '320px', height: '700px' }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What to Expect Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-[#1B365D] mb-6">
                  What to Expect on Our Call
                </h2>
                <p className="text-xl text-[#2A2D34]">
                  Here's how we'll spend our 30 minutes together to maximize value for your business
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#2BB6A3]/10 rounded-lg mr-4 flex-shrink-0">
                      <span className="text-[#2BB6A3] font-bold">1</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1B365D] mb-2">Business Assessment</h3>
                      <p className="text-[#2A2D34] text-sm">We'll review your current revenue model, growth challenges, and identify immediate opportunities.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#2BB6A3]/10 rounded-lg mr-4 flex-shrink-0">
                      <span className="text-[#2BB6A3] font-bold">2</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1B365D] mb-2">Gap Analysis</h3>
                      <p className="text-[#2A2D34] text-sm">Identify what's preventing you from achieving predictable, scalable revenue growth.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#2BB6A3]/10 rounded-lg mr-4 flex-shrink-0">
                      <span className="text-[#2BB6A3] font-bold">3</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1B365D] mb-2">Custom Strategy</h3>
                      <p className="text-[#2A2D34] text-sm">Receive a tailored roadmap with specific next steps to accelerate your growth.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-[#1B365D] to-[#2BB6A3] rounded-xl p-8 text-white">
                  <h3 className="text-xl font-bold mb-6">About James Hansen</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <i className="ri-check-line text-[#D4A017] mr-3"></i>
                      <span className="text-sm">12+ years in revenue optimization</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-check-line text-[#D4A017] mr-3"></i>
                      <span className="text-sm">Helped 75+ companies scale predictably</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-check-line text-[#D4A017] mr-3"></i>
                      <span className="text-sm">Average 300% revenue increase</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-check-line text-[#D4A017] mr-3"></i>
                      <span className="text-sm">Former management consultant</span>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-6 border-t border-white/20">
                    <p className="text-sm opacity-90">
                      "I've been where you are - struggling with unpredictable revenue. Let me show you the exact systems that transformed my business and can do the same for yours."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-[#F6F7F9]">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold text-[#1B365D] text-center mb-12">
                Frequently Asked Questions
              </h2>
              
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="font-semibold text-[#1B365D] mb-2">Is this really free?</h3>
                  <p className="text-[#2A2D34] text-sm">Yes, absolutely. This is a genuine strategy session with no hidden costs or obligations.</p>
                </div>
                
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="font-semibold text-[#1B365D] mb-2">What if I'm not ready to work together?</h3>
                  <p className="text-[#2A2D34] text-sm">No problem at all. You'll still receive valuable insights and a clear roadmap you can implement on your own.</p>
                </div>
                
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="font-semibold text-[#1B365D] mb-2">How should I prepare for the call?</h3>
                  <p className="text-[#2A2D34] text-sm">Come with your current revenue numbers, biggest challenges, and growth goals. The more specific, the better we can help.</p>
                </div>
                
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="font-semibold text-[#1B365D] mb-2">What happens after our call?</h3>
                  <p className="text-[#2A2D34] text-sm">You'll receive a summary of our discussion and recommended next steps. If there's a fit, we'll discuss how we can work together.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
